import { ANALYTICS_HOST } from 'src/config';
import { CHAT_CLASS, CHAT_SOURCE_TYPES } from 'src/types/enum';

import {
  CHAT_CLASS_ASSISTANT,
  CHAT_CLASS_DATA_ANALYST,
  CHAT_CLASS_DEFAULT,
  CHAT_CLASS_SMART_MEET,
  CHAT_QUERY_SUBMITTED,
  CHAT_TOOL_EXECUTED,
  CHAT_TYPE_ENTERPRISE,
  CHAT_TYPE_WORLD_KNOWLEDGE,
  TOOL_ATTACHED_RESOURCES,
  TOOL_CANVAS,
  TOOL_IMAGE_GENERATION,
  TOOL_LIBRARY_RESOURCES,
  TOOL_PRO_SEARCH,
  TOOL_REGENERATE_RESPONSE,
  TOOL_STICK_TO_DOCUMENTS,
  TOOL_WEB_SEARCH
} from './analyticsConstants';

export * from './analyticsConstants';

export const getSubdomain = () => {
  const path = window.location.hostname.split('.');
  const subdomain = path && path.length ? path[0]?.toLowerCase() : 'default';
  return subdomain;
};

export const getExternalAnalyticsConfig = () => {
  return {
    api_host: ANALYTICS_HOST,
    defaults: '2025-11-30',
    person_profiles: 'never',
    autocapture: false,
    capture_pageview: false,
    cookieless_mode: 'always',
    persistence: 'memory',
    respect_dnt: true, // Respect browser's Do Not Track setting
    mask_all_text: true, // Mask all text to prevent PII capture
    mask_all_element_attributes: false, // Mask element attributes It doesn't capture any events with any attributes.
    disable_session_recording: true, // Disable session recording by default

    // Exception and debug settings
    capture_exceptions: true,
    debug: import.meta.env.MODE === 'development'
  } as const;
};

export const getChatTypeName = (type: string) => {
  if (type === CHAT_SOURCE_TYPES.EmpowerGPT) {
    return CHAT_TYPE_ENTERPRISE;
  } else if (type === CHAT_SOURCE_TYPES.ChatGPT) {
    return CHAT_TYPE_WORLD_KNOWLEDGE;
  }
  return '-';
};

export const getChatClass = (c_class: string, isDataAnalyst?: boolean) => {
  if (c_class === CHAT_CLASS.ASSISTANT) {
    return CHAT_CLASS_ASSISTANT;
  } else if (c_class === CHAT_CLASS.SMART_MEET) {
    return CHAT_CLASS_SMART_MEET;
  } else if (c_class === CHAT_CLASS.DATA_ANALYST || isDataAnalyst) {
    return CHAT_CLASS_DATA_ANALYST;
  }
  return CHAT_CLASS_DEFAULT;
};

export interface IToolExecutionProp {
  selected_tools?: string[];
  selected_tool?: string;
  selected_model: string;
  chat_type: string;
  chat_class: string;
}

export const getSelectedTool = (p) => {
  const selectedTool = [];

  if (p.web_search_enabled) {
    selectedTool.push(TOOL_WEB_SEARCH);
  }
  if (p.pro_search_enabled) {
    selectedTool.push(TOOL_PRO_SEARCH);
  }
  if (p.image_generation_enabled) {
    selectedTool.push(TOOL_IMAGE_GENERATION);
  }
  if (p.document_context) {
    selectedTool.push(TOOL_STICK_TO_DOCUMENTS);
  }
  if (p.canvas) {
    selectedTool.push(TOOL_CANVAS);
  }
  if (p.resume) {
    selectedTool.push(TOOL_REGENERATE_RESPONSE);
  }
  if (p.chat_folder_name || p.new_files) {
    selectedTool.push(TOOL_ATTACHED_RESOURCES);
  }
  let chatResourcesArr = [];
  if (typeof p.chat_resources === 'string') {
    try {
      chatResourcesArr = JSON.parse(p.chat_resources);
    } catch (e) {
      chatResourcesArr = [];
    }
  } else if (Array.isArray(p.chat_resources)) {
    chatResourcesArr = p.chat_resources;
  }

  if (Array.isArray(chatResourcesArr) && chatResourcesArr.length) {
    selectedTool.push(TOOL_LIBRARY_RESOURCES);
  }
  return selectedTool;
};

export const toolExecutionEvent = (
  apiParams,
  chat_context,
  collectAnalytics
) => {
  const tools_selected = getSelectedTool(apiParams);
  const event_data: IToolExecutionProp = {
    selected_model: chat_context?.chatModel?.displayName || '-',
    chat_type: getChatTypeName(apiParams?.chat_type),
    chat_class: getChatClass(apiParams?.chat_class)
  };
  for (const tool of tools_selected) {
    collectAnalytics(CHAT_TOOL_EXECUTED, {
      ...event_data,
      selected_tool: tool
    });
  }
  event_data['selected_tools'] = tools_selected;
  collectAnalytics(CHAT_QUERY_SUBMITTED, event_data);
  return event_data;
};
